import { compose } from 'recompose';
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import withFeatureFlag from '../Auth/withFeatureFlag';
import { SAAS_V1 } from '@/constants/featureFlags';
import { useMutation } from '@apollo/client';
import { UPSERT_ORDERS } from '@/components/ShipmentForm/graphql/mutations';
import * as Sentry from '@sentry/react';
import EditOrderModal from '@/components/ShipmentForm/modals/EditOrderModal';
import { EDIT_ORDER_TABS } from '@/components/ShipmentForm/constants';
import { PlanningDataContext, PlanningSelectionContext, PlanningUiContext, withPlanningContext } from './context';
import { QUERY_PLANNING_DATA } from './graphql/queries';
import { ELIGIBILITY, appendBucketRows, bucketEligibility, readBucketOrder } from './graphql/planningBuckets';
import { ORDER_ROUTE_MAPPINGS } from '@/graphql/queries/order_route_mappings';
import { Grid, IconButton, Tab, Tabs, Tooltip } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import PlanningHeader from './PlanningHeader';
import PlanningNotification from './PlanningNotification';
import PlanningOrders from './orders/PlanningOrders';
import PlanningRoutes from './routes/PlanningRoutes';
import { MODALS } from './constants';
import { css } from '@emotion/react';
import { FullHeightGrid, GrowingGrid } from './blocks';
import hubspotControls from '@/utilities/hubspotControls';
import PlanningStops from './stops/PlanningStops';
import OrdersToUnassigned from './modals/OrdersToUnassigned';
import DriverOverlapModal from './modals/DriverOverlapModal';
import OrdersUnassignedToOnward from './modals/OrdersUnassignedToOnward';
import RouteRemoveModal from './modals/RouteRemoveModal';
import RouteUnlockModal from './modals/RouteUnlockModal';
import RouteRescheduleModal from './modals/RouteRescheduleModal';
import StopRemoveModal from './modals/StopRemoveModal';
import CustomTimeframeModal from './modals/CustomTimeframes';
import AssignToRoute from './map/AssignToRoute';
import { useAutoAssignToRoute } from './map/useAssignToRoute';
import PlanningMap from './map/PlanningMap';
import RouteSubmitOnwardModal from './modals/RouteSubmitOnwardModal';
import RouteSubmitInternalModal from './modals/RouteSubmitInternalModal';
import OrderViewModal from './modals/OrderViewModal';
import CommunicationModal from './modals/CommunicationModal';
import RouteExportModal from './modals/RouteExportModal';
import SendToOnwardModal from './modals/SendToOnwardModal';
import { useClientUser } from '@/hooks';
import SettingsModal from './modals/SettingsModal';
import DriverLunchModal from './modals/DriverLunchModal';
import OrderClaimTCModal from '../OrderClaimTCModal';
import NotesModal from './modals/NotesModal';
import { useCallbacks } from './hooks';
import ClaimLoads from '../LoadBoardLTL/ClaimLoads';
import PlaceBidModal from '../LoadBoardLTL/PlaceBidModal';
import StopWindowModal from './modals/StopWindowModal';
import OrdersReschedule from './modals/OrdersReschedule';
import OrdersAwaitingCDModal from './modals/OrdersAwaitingCDModal';
import OrdersOffResourceModal from './modals/OrdersOffResourceModal';
import BatchTimeframeConfirmationModal from './modals/BatchTimeframeConfirmationModal';

// Drag bounds for the tabs (Routes/Orders list) column, in px.
const TABS_PANEL_MIN_WIDTH = 360;
const TABS_PANEL_MAX_WIDTH = 600;
const TABS_PANEL_DEFAULT_WIDTH = 365;

// Drag bounds for the route-details (PlanningStops) panel, in px.
const STOPS_PANEL_MIN_WIDTH = 280;
const STOPS_PANEL_MAX_WIDTH = 640;
const STOPS_PANEL_DEFAULT_WIDTH = 350;

// The map must always keep at least this much width — dragging a panel wider
// stops here rather than starving the map (which can shrink to 0). Without this
// the two panels' hardcoded maxes can sum past a narrow container.
const MIN_MAP_WIDTH = 320;

// Thin grab strip on the right edge of a panel. Click-and-drag resizes the
// host panel (its `position: relative` parent), which drives its width from the
// same state via `setWidth`.
//
// - Uses Pointer Events + setPointerCapture so the drag keeps tracking when the
//   cursor leaves the window / crosses to another screen, and always gets a
//   terminal event (pointerup / lostpointercapture) so it can't get stuck.
// - During the drag we mutate the panel's width directly on the DOM and only
//   commit to React state on release, so we don't re-render the whole planning
//   tree (header/routes/stops/map) on every pointer move.
// - The effective max is clamped against the live container width so the panels
//   can't collectively starve the map on smaller screens.
const ResizeHandle = ({ width, setWidth, min, max }) => {
    const onPointerDown = useCallback(
        (e) => {
            // Only a primary (left) button drag — ignore right/middle clicks.
            if (e.button !== 0) return;
            e.preventDefault();

            const handle = e.currentTarget;
            const panel = handle.parentElement;
            const container = panel?.parentElement;
            if (!panel || !container) return;

            const startX = e.clientX;
            const startWidth = width;

            // Reserve room for the map: the most this panel may take is the
            // container width minus MIN_MAP_WIDTH minus the other non-growing
            // sibling panels (the map is flex-grow: 1, so we skip it). These
            // don't change during this drag, so measure once up front.
            let fixedSiblings = 0;
            for (const child of container.children) {
                if (child === panel) continue;
                if (parseFloat(getComputedStyle(child).flexGrow) > 0) continue;
                fixedSiblings += child.getBoundingClientRect().width;
            }
            const containerMax = container.clientWidth - MIN_MAP_WIDTH - fixedSiblings;
            const effectiveMax = Math.max(min, Math.min(max, containerMax));
            const clamp = (w) => Math.min(effectiveMax, Math.max(min, w));

            let latest = clamp(startWidth);

            const onMove = (moveEvent) => {
                latest = clamp(startWidth + (moveEvent.clientX - startX));
                panel.style.width = `${latest}px`;
            };
            const onUp = () => {
                handle.removeEventListener('pointermove', onMove);
                handle.removeEventListener('pointerup', onUp);
                handle.removeEventListener('lostpointercapture', onUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                // Commit the final width to state once, so it persists and React
                // stays in sync with the DOM we've been mutating directly.
                setWidth(latest);
            };

            handle.setPointerCapture(e.pointerId);
            handle.addEventListener('pointermove', onMove);
            handle.addEventListener('pointerup', onUp);
            handle.addEventListener('lostpointercapture', onUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },
        [width, setWidth, min, max]
    );

    return (
        <div
            onPointerDown={onPointerDown}
            css={css`
                position: absolute;
                top: 0;
                right: -3px;
                width: 6px;
                height: 100%;
                cursor: col-resize;
                z-index: 5;
                touch-action: none;
                &:hover,
                &:active {
                    background-color: rgba(89, 184, 99, 0.4);
                }
            `}
        />
    );
};

// Small floating notch that pops out / collapses the PlanningStops
// route-details panel. Absolutely positioned in the top-right corner of the
// routes list (its `position: relative` container) so it doesn't reserve a
// full-height layout column, and stays put at the list/details boundary
// whether the panel is open or collapsed.
const StopsPanelToggle = ({ open, onToggle }) => (
    <Tooltip title={open ? 'Collapse route details' : 'Expand route details'} placement="right">
        <IconButton
            size="small"
            onClick={onToggle}
            css={css`
                position: absolute;
                top: 61px;
                right: -17px;
                z-index: 6;
                border: 1px solid #e2e2e2;
                background-color: #fafafa;
                &:hover {
                    background-color: #f0f0f0;
                }
            `}
        >
            {open ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
    </Tooltip>
);

const DispatchPlan = () => {
    const { user_id, circles } = useClientUser();
    const [tabIndex, setTabIndex] = useState(0);
    const [editing, setEditing] = useState(null);
    // Whether the route-details pop-out is expanded. Defaults to collapsed so
    // selecting a route doesn't auto-open it; persists across route selections
    // since DispatchPlan stays mounted the whole time.
    const [stopsPanelOpen, setStopsPanelOpen] = useState(true);
    // Drag-resizable widths of the two left columns; persist across route
    // selections since DispatchPlan stays mounted.
    const [tabsPanelWidth, setTabsPanelWidth] = useState(TABS_PANEL_DEFAULT_WIDTH);
    const [stopsPanelWidth, setStopsPanelWidth] = useState(STOPS_PANEL_DEFAULT_WIDTH);

    const { selectedOrders, selectedRoute, setSelectedRoute, setSelectedLoads, selectedLoadBoardLoads } =
        useContext(PlanningSelectionContext);
    const { setModalOpen, modalOpen, setError, setNotification, actionableOrder, bid, setBid } =
        useContext(PlanningUiContext);
    const {
        state: { routes, variables },
        callbacks: { refetch },
    } = useContext(PlanningDataContext);

    const { placeBid, claimNow } = useCallbacks();

    // When a route is selected, selecting orders (list / map marker / lasso)
    // assigns them straight to that route — no dropdown or Assign click needed.
    useAutoAssignToRoute();

    const handleTabChange = (tab) => {
        setTabIndex(tab);
        if (selectedRoute) setSelectedRoute(null);
    };

    useEffect(() => {
        hubspotControls.hide();

        return () => {
            hubspotControls.show();
        };
    }, []);

    const [submitOrder] = useMutation(UPSERT_ORDERS, {
        onCompleted: () => {
            setModalOpen(null);
        },
        // Bucket membership is the only thing an edit can change that the
        // mutation's own returning can't paint in place (entity scalars are
        // already normalized). `context.preEditOrder` must be captured before
        // mutate — by the time update() runs, the document write has already
        // overwritten the cached order, so a cache read here would see only
        // post-edit values. Eligibility (incl. the DISCARD-mapping exclusion)
        // is delegated to the same shared module StopRemoveModal's unassign
        // flow uses (./graphql/planningBuckets), so entry can be admitted
        // directly instead of always falling back to a refetch.
        update: (cache, { data }, { context }) => {
            const post = data?.insert_orders?.returning?.[0];
            const pre = context?.preEditOrder;

            // delete_items.returning is item_id-only (no doc change needed);
            // eviction is belt-and-braces for other pages holding item refs
            // now that the order's own items list has moved on.
            for (const { item_id } of data?.delete_items?.returning || []) {
                cache.evict({ id: cache.identify({ __typename: 'items', item_id }) });
            }

            if (!post || !pre) return;

            const resolveMapping = (mapping) =>
                cache.readFragment({
                    id: cache.identify({ __typename: 'order_route_mappings', mapping_id: mapping.mapping_id }),
                    fragment: ORDER_ROUTE_MAPPINGS,
                });
            // UPSERT_ORDERS touches no mappings — the order's slim routes list
            // (absent from the mutation's OrderFields-only returning) is
            // unchanged by this edit, so the pre-edit snapshot is authoritative.
            const postWithRoutes = { ...post, routes: pre.routes };

            let needsRefetch = false;
            cache.updateQuery({ query: QUERY_PLANNING_DATA, variables }, (prev) => {
                if (!prev?.pickups || !prev?.dropoffs) return prev;

                let pickups = prev.pickups;
                let dropoffs = prev.dropoffs;

                for (const bucket of ['pickups', 'dropoffs']) {
                    const list = bucket === 'pickups' ? prev.pickups : prev.dropoffs;
                    const inBucket = list.some((o) => o.order_id === post.order_id);
                    const eligibility = bucketEligibility(postWithRoutes, { bucket, variables, resolveMapping });

                    // Unresolvable can't be trusted either way — leave the row
                    // as-is (whether in the bucket or not) and fall back to a
                    // refetch instead of guessing.
                    if (eligibility === ELIGIBILITY.UNRESOLVABLE) {
                        needsRefetch = true;
                        continue;
                    }

                    if (inBucket && eligibility === ELIGIBILITY.INELIGIBLE) {
                        const filtered = list.filter((o) => o.order_id !== post.order_id);
                        if (bucket === 'pickups') pickups = filtered;
                        else dropoffs = filtered;
                    } else if (!inBucket && eligibility === ELIGIBILITY.ELIGIBLE) {
                        const row = readBucketOrder(cache, post.order_id);
                        if (!row) {
                            needsRefetch = true;
                        } else if (bucket === 'pickups') {
                            pickups = appendBucketRows(pickups, [row]);
                        } else {
                            dropoffs = appendBucketRows(dropoffs, [row]);
                        }
                    }
                }

                return { ...prev, pickups, dropoffs };
            });

            if (needsRefetch) {
                refetch();
            }
        },
        onError: (e) => {
            setError('Failed to update order. Please try again.');
            Sentry.captureException(e);
        },
    });

    const onEdit = useCallback(
        ({
            errors,
            order: modified,
            items: itemsModified,
            itemsRemoved,
            itemUpdates,
            tags,
            tagsRemoved,
            documents,
            orderIds,
            subscriptions,
        }) => {
            if (errors?.hasMissingField?.length) {
                setNotification({
                    severity: 'error',
                    message: `Order has missing fields: ${errors.hasMissingField.join(', ')}.`,
                });
            } else if (errors?.phoneInvalid) {
                setNotification({ severity: 'error', message: 'Order has an invalid phone number.' });
            } else {
                if (errors?.failedPricing?.length) {
                    let message = `Failed to calculate pricing for ${modified.order_number}.`;
                    if (errors.failedPricing.includes('miles')) {
                        message = `${message} Cannot find distance between pickup and dropoff locations.`;
                    }
                    if (errors.failedPricing.includes('delivery_type')) {
                        message = `${message} ${
                            modified.order_type === 'return' ? 'Return Pickup' : 'Delivery'
                        } type not set.`;
                    }
                    setNotification({ severity: 'warning', message });
                }

                const { notes, ...rest } = modified;

                submitOrder({
                    // The pre-edit snapshot for the bucket-membership patch —
                    // must be captured here, before mutate, since update() runs
                    // after the document write has already overwritten the
                    // cached order.
                    context: { preEditOrder: actionableOrder },
                    variables: {
                        orders: [
                            {
                                ...rest,
                                notes: {
                                    data: notes,
                                    on_conflict: {
                                        constraint: 'notes_pkey',
                                        update_columns: ['note'],
                                    },
                                },
                            },
                        ],
                        items: itemsModified,
                        removals: itemsRemoved,
                        item_updates: itemUpdates || [],
                        tags: tags,
                        tag_removals: tagsRemoved,
                        order_ids: orderIds,
                        documents: documents,
                        email_subscriptions: subscriptions || [],
                    },
                });
                setEditing(null);
            }
        },
        [actionableOrder]
    );

    return (
        <FullHeightGrid
            container
            direction="column"
            wrap="nowrap"
            css={css`
                background-color: white;
                height: 100%;
            `}
        >
            <Grid item>
                <PlanningHeader />
            </Grid>

            <GrowingGrid container>
                {/* Tabs - Orders / Routes */}
                <FullHeightGrid
                    item
                    style={{ width: tabsPanelWidth }}
                    css={css`
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        flex-wrap: nowrap;
                        border: 1px solid #e2e2e2;
                        flex: 0 0 auto;
                        min-width: ${TABS_PANEL_MIN_WIDTH}px;
                        max-width: ${TABS_PANEL_MAX_WIDTH}px;
                    `}
                >
                    <Grid container justifyContent="center">
                        <Tabs
                            value={tabIndex}
                            onChange={(event, tab) => handleTabChange(tab)}
                            textColor="primary"
                            indicatorColor="primary"
                            // centered
                        >
                            <Tab label="Routes" value={0} style={{ outline: 0 }} />
                            <Tab label="Orders" value={1} style={{ outline: 0 }} />
                        </Tabs>
                    </Grid>

                    <GrowingGrid container direction="column" wrap="nowrap">
                        {tabIndex === 0 ? <PlanningRoutes /> : <PlanningOrders />}
                    </GrowingGrid>

                    {/* Route-details pop-out toggle (small notch, no full-height rail) */}
                    {tabIndex === 0 && selectedRoute && (
                        <StopsPanelToggle open={stopsPanelOpen} onToggle={() => setStopsPanelOpen((prev) => !prev)} />
                    )}

                    <ResizeHandle
                        width={tabsPanelWidth}
                        setWidth={setTabsPanelWidth}
                        min={TABS_PANEL_MIN_WIDTH}
                        max={TABS_PANEL_MAX_WIDTH}
                    />
                </FullHeightGrid>

                {/* Stops (collapsible pop-out) */}
                {tabIndex === 0 && selectedRoute && stopsPanelOpen && (
                    <FullHeightGrid
                        item
                        style={{ width: stopsPanelWidth }}
                        css={css`
                            position: relative;
                            display: flex;
                            flex-direction: column;
                            flex-wrap: nowrap;
                            border: 1px solid #e2e2e2;
                            flex: 0 0 auto;
                            min-width: ${STOPS_PANEL_MIN_WIDTH}px;
                            max-width: ${STOPS_PANEL_MAX_WIDTH}px;
                        `}
                    >
                        <PlanningStops />
                        <ResizeHandle
                            width={stopsPanelWidth}
                            setWidth={setStopsPanelWidth}
                            min={STOPS_PANEL_MIN_WIDTH}
                            max={STOPS_PANEL_MAX_WIDTH}
                        />
                    </FullHeightGrid>
                )}

                {/* Map */}
                <FullHeightGrid item xs>
                    <PlanningMap />
                    {Object.values(selectedOrders).filter((x) => !!x).length > 0 && <AssignToRoute />}
                    {selectedLoadBoardLoads.length > 0 && (
                        <ClaimLoads
                            loads={selectedLoadBoardLoads}
                            callbacks={{
                                setPlaceBidModalOpen: () => setModalOpen(MODALS.PLACE_BID),
                                setTCModalOpen: () => setModalOpen(MODALS.CLAIM_TC),
                                onClose: () => setSelectedLoads({}),
                            }}
                            css={css`
                                // profile header + planning header + padd
                                top: 140px;
                            `}
                        />
                    )}
                </FullHeightGrid>
            </GrowingGrid>

            {/* Route Modals */}
            <RouteRemoveModal />
            <RouteUnlockModal />
            <RouteRescheduleModal />
            <RouteExportModal />
            <RouteSubmitOnwardModal />
            <RouteSubmitInternalModal />

            {/* Stop Modals */}
            <StopRemoveModal />
            <CommunicationModal />
            <CustomTimeframeModal />
            {/* <NotesModal /> */}
            <StopWindowModal />

            {/* Order Modals */}
            <OrdersOffResourceModal />
            <OrdersToUnassigned />
            <OrdersUnassignedToOnward />
            <OrderViewModal />
            <OrdersReschedule />
            <BatchTimeframeConfirmationModal />
            <EditOrderModal
                order={actionableOrder}
                open={modalOpen === MODALS.ORDER_EDIT || modalOpen === MODALS.NOTES}
                initTab={modalOpen === MODALS.NOTES ? EDIT_ORDER_TABS.NOTES : EDIT_ORDER_TABS.PICKUP}
                tabs={modalOpen === MODALS.NOTES ? [EDIT_ORDER_TABS.NOTES] : null}
                callbacks={{
                    onSave: onEdit,
                    onClose: () => {
                        setModalOpen(null);
                    },
                    onError: (error) => {
                        setError(error);
                    },
                }}
                opt={{
                    disableAddressEditing: actionableOrder?.routes?.length > 0,
                    readOnly: !actionableOrder?.oms && !!actionableOrder?.carrier_invoice_id,
                }}
            />

            {/* Load Board Modals */}
            <SendToOnwardModal />
            <PlaceBidModal
                load={selectedLoadBoardLoads?.[0]}
                open={modalOpen === MODALS.PLACE_BID}
                onClose={() => {
                    setModalOpen(null);
                    setBid(null);
                }}
                callbacks={{
                    placeBid: (bid) => {
                        setBid(bid);
                        setModalOpen(MODALS.CLAIM_TC);
                    },
                }}
            />
            <OrderClaimTCModal
                loads={selectedLoadBoardLoads}
                open={modalOpen === MODALS.CLAIM_TC}
                bid={bid}
                onClose={() => {
                    setModalOpen(null);
                    setBid(null);
                }}
                callbacks={{
                    claimNow: (loads, deliveryDate, targetDeliveryDate, locationId) => {
                        claimNow({
                            carrier_id: user_id,
                            order_ids: loads.map((load) => load.order_id),
                            delivery_date: deliveryDate,
                            target_delivery_date: targetDeliveryDate,
                            planning: true,
                            locationId: locationId,
                        });
                    },
                    placeBid: (bid, locationId) => {
                        placeBid({
                            carrier_id: user_id,
                            bid,
                            locationId: locationId,
                        });
                    },
                }}
            />

            {/* Misc Modals */}
            <SettingsModal />
            <DriverOverlapModal />
            <DriverLunchModal />
            <OrdersAwaitingCDModal
                open={modalOpen === MODALS.AWAITING_CD}
                onClose={() => {
                    setModalOpen(null);
                }}
                route={routes.find((route) => route.route_id === selectedRoute)}
            />

            {/* Notification */}
            <PlanningNotification />
        </FullHeightGrid>
    );
};

export default compose(withFeatureFlag(SAAS_V1), withPlanningContext)(DispatchPlan);
